import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { renderSbIcon } from '../IconPickerField';
import { sfHeadline, sfSub } from './sectionHelpers';

interface Props {
  section: SectionInstance;
}

const ProcessSteps: React.FC<Props> = ({ section }) => {
  const c = section.controls;
  const variant = c.variant || 'horizontal';
  const isHorizontal = variant === 'horizontal';
  const steps = (c.steps || [
    { number: '01', title: 'Discovery', description: 'We learn about your goals and challenges.', icon: '🔍' },
    { number: '02', title: 'Strategy', description: 'We craft a plan tailored to your needs.', icon: '🗺' },
    { number: '03', title: 'Execution', description: 'Our team delivers with precision and care.', icon: '⚡' },
    { number: '04', title: 'Results', description: 'You achieve measurable, lasting outcomes.', icon: '🏆' },
  ]) as Array<{ number: string; title: string; description: string; icon: string }>;

  return (
    <section className="sb-render sb-render-process-steps" style={{ padding: '80px 24px', background: 'var(--brand-bg, #fff)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: 12, letterSpacing: '-0.025em' }}
          >
            {sfHeadline(c, 'Our Process')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subheadline"
            style={{ fontSize: '1.125rem', color: 'var(--brand-text-light, #64748b)', maxWidth: 560, margin: '0 auto' }}
          >
            {sfSub(c, 'A proven approach to delivering exceptional results.')}
          </InlineEditText>
        </div>

        {/* Steps */}
        {isHorizontal ? (
          <div style={{ position: 'relative' }}>
            {/* Connecting line */}
            <div style={{
              position: 'absolute',
              top: 32,
              left: '10%',
              right: '10%',
              height: 2,
              background: 'linear-gradient(90deg, var(--brand-primary, #2563eb), var(--brand-secondary, #7c3aed))',
              zIndex: 0,
            }} />
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, 1fr)`, gap: 24, position: 'relative', zIndex: 1 }}>
              {steps.map((step, i) => (
                <div key={i} style={{ textAlign: 'center', padding: '0 12px' }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--brand-primary, #2563eb), var(--brand-secondary, #7c3aed))',
                    color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontSize: '1.5rem',
                    boxShadow: '0 4px 20px rgba(37,99,235,0.35)',
                    border: '4px solid var(--brand-bg, #fff)',
                  }}>
                    {renderSbIcon(step.icon, 28) ?? step.number}
                  </div>
                  <div style={{
                    display: 'inline-block',
                    fontSize: '0.75rem', fontWeight: 700,
                    color: 'var(--brand-primary, #2563eb)',
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    marginBottom: 6,
                  }}>
                    Step {step.number || i + 1}
                  </div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 8 }}>{step.title}</h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--brand-text-light, #64748b)', lineHeight: 1.6 }}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {steps.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                {/* Left: number + line */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 64 }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--brand-primary, #2563eb), var(--brand-secondary, #7c3aed))',
                    color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.25rem', fontWeight: 700, flexShrink: 0,
                    boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
                  }}>
                    {renderSbIcon(step.icon, 24) ?? step.number ?? i + 1}
                  </div>
                  {i < steps.length - 1 && (
                    <div style={{
                      width: 2, flex: 1, minHeight: 40,
                      background: 'linear-gradient(180deg, var(--brand-primary, #2563eb), rgba(37,99,235,0.2))',
                      margin: '8px 0',
                    }} />
                  )}
                </div>
                {/* Right: content */}
                <div style={{ padding: '4px 0 48px' }}>
                  <div style={{
                    fontSize: '0.75rem', fontWeight: 700,
                    color: 'var(--brand-primary, #2563eb)',
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    marginBottom: 4,
                  }}>
                    Step {step.number || i + 1}
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>{step.title}</h3>
                  <p style={{ color: 'var(--brand-text-light, #64748b)', lineHeight: 1.7, maxWidth: 560 }}>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProcessSteps;
